import { Link } from "react-router-dom";
import { ArrowRight, Brain, Cloud, ShieldCheck, Atom, GitBranch, Repeat, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const HERO_IMG = "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80";

const practices = [
  {
    icon: Brain, color: "var(--accent-purple)",
    title: "AI / ML & Data Engineering",
    tag: "Hottest demand 2025",
    body: "The #1 talent gap in Canada. From LLM fine-tuning engineers to MLOps architects and data annotators.",
    roles: ["Generative AI / prompt engineers", "ML platform & MLOps engineers", "AI product managers", "Data annotation & RLHF leads", "Vector DB & RAG specialists"],
  },
  {
    icon: Cloud, color: "var(--accent-teal)",
    title: "Cloud & Platform Engineering",
    tag: "Infrastructure boom",
    body: "AWS, Azure, GCP architects and FinOps engineers are in shortage as enterprises migrate and scale.",
    roles: ["Cloud architects (multi-cloud)", "Platform / SRE engineers", "Kubernetes & Terraform leads", "FinOps & cloud cost engineers", "Database reliability (Postgres, Mongo)"],
  },
  {
    icon: ShieldCheck, color: "var(--accent-amber)",
    title: "Cybersecurity & GRC",
    tag: "Post-2024 breach cycle",
    body: "Every regulated sector — banking, healthcare, insurance — is hiring aggressively post-2024 breach cycles.",
    roles: ["CISO-as-a-Service placements", "SOC analysts (L1–L3)", "Penetration testers / red team", "GRC / compliance analysts", "Zero-trust architects"],
  },
  {
    icon: Atom, color: "var(--accent-mint)",
    title: "Quantum & Emerging Tech",
    tag: "Canadian advantage",
    body: "Canada is a global leader in quantum. Early-mover positioning attracts academic spinouts and deep-tech VC portfolios.",
    roles: ["Quantum algorithm researchers", "Photonics & hardware engineers", "HPC / scientific computing", "Edge AI specialists"],
  },
  {
    icon: GitBranch, color: "var(--accent-teal)",
    title: "DevSecOps & Automation",
    tag: "Shift-left demand",
    body: "Security baked into pipelines. Organizations shifting left need talent that spans dev, sec, and ops simultaneously.",
    roles: ["DevSecOps pipeline engineers", "CI/CD architects", "Security tooling engineers", "Shift-left QA automation"],
  },
  {
    icon: Repeat, color: "var(--accent-purple)",
    title: "Digital Transformation",
    tag: "Legacy modernization",
    body: "Legacy modernization, ERP migrations, and digital product builds drive demand for cross-functional talent.",
    roles: ["Change management leads", "ERP functional consultants (SAP/Oracle)", "Product owners & agile coaches", "API integration architects"],
  },
];

export default function TechStaffing() {
  return (
    <div data-testid="tech-staffing-page">
      <PageHero
        eyebrow="Technology Staffing"
        title={
          <>
            We staff the roles your{" "}
            <span className="italic" style={{ color: "var(--accent-purple)" }}>recruiters can&apos;t fill.</span>
          </>
        }
        subtitle="Ashren specializes in the top 5% of niche technical talent that generic staffing firms pass over. Our sourcing reaches GitHub contributors, Kaggle grandmasters, and the conference circuit — not just LinkedIn's active applicants."
        image={HERO_IMG}
        imageAlt="Modern technology workspace"
        testId="tech-hero"
      />

      <section className="py-16 md:py-24" data-testid="tech-practices-list">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 space-y-5">
          {practices.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="card grid md:grid-cols-12 gap-8 items-start" data-testid={`tech-practice-${i}`}>
                <div className="md:col-span-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={24} />
                  </div>
                  <div className="eyebrow mb-2" style={{ color: p.color }}>{p.tag}</div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight mb-3">{p.title}</h2>
                  <p className="text-[color:var(--muted)] leading-relaxed">{p.body}</p>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)] mb-4">
                    Representative roles we place
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2 text-[color:var(--primary-dark)]">
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: p.color }} />
                        <span className="text-sm">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTABand
        title="Brief us on your next critical tech hire."
        subtitle="Share the role — we'll return with a market read, compensation benchmark, and a shortlist in under 5 business days."
        primaryLabel="Request a talent brief"
        primaryTo="/for-employers"
        secondaryLabel="Download the salary guide"
        secondaryTo="/resources"
      />
    </div>
  );
}
